import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { FadeUp } from "./Reveal";
import { getGatherings } from "@/data/gatherings";

const EventCard = ({ g, upcoming, testid }) => (
  <Link
    to={`/gatherings/${g.slug}`}
    className="group relative block overflow-hidden rounded-sm"
    data-testid={testid}
  >
    <div className="relative aspect-[16/9] w-full overflow-hidden bg-black">
      <img
        src={g.cover}
        alt={g.title}
        loading="lazy"
        className="h-full w-full object-cover opacity-95 transition-transform duration-[1600ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.05]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/15 to-transparent" />

      {/* Hover affordance */}
      <span className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-full border border-gaia-bg/30 text-gaia-bg opacity-0 transition-all duration-500 group-hover:opacity-100">
        <ArrowUpRight size={16} strokeWidth={1.5} />
      </span>

      <div className="absolute inset-x-0 bottom-0 p-6 md:p-7">
        {upcoming ? (
          <span className="gaia-glow inline-flex items-center rounded-full bg-gaia-terracotta px-3 py-1 font-sans text-[10px] uppercase tracking-[0.24em] text-gaia-bg">
            Coming up
          </span>
        ) : (
          <span className="font-sans text-[10px] uppercase tracking-[0.28em] text-gaia-bg/60">
            Our last gathering
          </span>
        )}
        <h3 className="mt-3 font-serif text-2xl font-light leading-snug text-gaia-bg md:text-[28px]">
          {g.title}
        </h3>
        <p className="mt-1.5 font-sans text-[11px] uppercase tracking-[0.2em] text-gaia-bg/70">
          {g.date}
        </p>
      </div>
    </div>
  </Link>
);

const GatheringsTeaser = () => {
  const all = getGatherings();
  const featured = all[0]; // the last (past) gathering
  const upcoming = all.find((g) => g.status === "upcoming");
  if (!featured) return null;

  return (
    <section
      id="gatherings"
      className="gaia-grain relative overflow-hidden bg-gaia-charcoal py-12 text-gaia-bg md:py-14"
      data-testid="gatherings-teaser"
    >
      <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12">
        {/* Header */}
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div className="md:max-w-2xl">
            <FadeUp>
              <span className="font-sans text-[11px] uppercase tracking-[0.36em] text-gaia-taupe">
                Gatherings
              </span>
            </FadeUp>
            <FadeUp delay={0.08}>
              <p className="mt-4 font-serif text-xl font-light leading-snug tracking-tight text-gaia-bg md:text-2xl">
                Join us for an intimate evening under the full moon — a space to slow down,
                reconnect and honour what has grown within you.
              </p>
            </FadeUp>
          </div>
          <FadeUp delay={0.14}>
            <Link
              to="/gatherings"
              className="group inline-flex items-center gap-2 whitespace-nowrap font-sans text-[11px] uppercase tracking-[0.24em] text-gaia-bg/70 transition-colors duration-500 hover:text-gaia-bg"
              data-testid="teaser-all-gatherings"
            >
              All gatherings
              <ArrowUpRight size={14} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </FadeUp>
        </div>

        {/* Side-by-side showcase — upcoming first, then the last gathering */}
        <FadeUp delay={0.1}>
          <div className="mt-8 grid grid-cols-1 gap-5 md:mt-10 md:grid-cols-2 md:gap-6">
            {upcoming && (
              <EventCard g={upcoming} upcoming testid="teaser-upcoming-image" />
            )}
            <EventCard g={featured} testid="teaser-featured-image" />
          </div>
        </FadeUp>
      </div>
    </section>
  );
};

export default GatheringsTeaser;
